// src/pages/AdsSupport/AdsInquiryWrite.jsx
import React, { useRef, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import SupportTabs from "../MyPage/components/SupportTabs";
import { SingleButton, Button, FillButton } from "../../assets/components/ButtonGroup";
import { HeaderIconL } from "../../assets/components/HeaderGroup";
import left from "../../assets/icons/Arrow/left.svg";
import { useFixedLayout } from "../../hooks/useFixedLayout";
import { SelectBoxB } from "../../assets/components/SelectGroup";
import PlaceholderB from "../../assets/components/PlaceholderB";
import img_delete from "../../assets/icons/System/delete.svg";
import { addAppMessageListener, postToApp } from "../../bridges/appBridge"; // ⬅ 추가
import Popup from "../../assets/components/PopupGroup";

const API = process.env.REACT_APP_FASTAPI_ADS_URL;

const INQUIRY_TYPES = [
  "토큰(구매·환불) 문의",
  "회원 정보 문의 / 수정 요청",
  "서비스 불편 문의",
  "기타 문의",
];

const DEFAULT_TYPE_MYINFO = "회원 정보 문의 / 수정 요청";

const MAX_FILES = 3;

export default function AdsInquiryWrite() {
  const navigate = useNavigate();
  const location = useLocation();

  const { name, phone, from } = location.state || {};


  // RN / 플랫폼 판별
  const ua =
    typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
  const isAndroid = useMemo(() => /Android/i.test(ua), [ua]);
  const isRN = typeof window !== "undefined" && !!window.ReactNativeWebView;
  const isAndroidRN = isAndroid && isRN;
  const isIOSRN = isRN && !isAndroidRN;

  const { headerRef, headerSpace, footerRef, bottomSpace } = useFixedLayout();

  // 기본값은 로그인 정보/스토어 정보에서 가져오면 더 좋음
  const initialInquiryType =
    from?.pathname?.toLowerCase().includes("myinfo") ? DEFAULT_TYPE_MYINFO : "";

  const [inquiryType, setInquiryType] = useState(initialInquiryType);
  const [isTypeSheetOpen, setIsTypeSheetOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [showGuide, setShowGuide] = useState(false);

  const [files, setFiles] = useState([]); // File[]
  const fileInputRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [isSizeLimitPopupOpen, setIsSizeLimitPopupOpen] = useState(false);
  const isFormComplete = !!inquiryType && !!title.trim() && !!content.trim();

  // 전체 파일 용량(기존 + 새로 추가)이 10MB를 넘는지 체크
  const isOverTotalLimit = (currentFiles, newFiles) => {
    const totalSize = [...currentFiles, ...newFiles].reduce(
      (sum, f) => sum + (f?.size || 0),
      0
    );
    const LIMIT_10MB = 10 * 1024 * 1024; // 10MB
    return totalSize > LIMIT_10MB;
  };

  // dataURL(base64) → File 변환
  const dataUrlToFile = (dataUrl, filename = "inquiry.jpg") => {
    try {
      const [header, base64] = dataUrl.split(",");
      const mime = header.match(/:(.*?);/)?.[1] || "image/jpeg";
      const binary = atob(base64);
      const len = binary.length;
      const u8 = new Uint8Array(len);
      for (let i = 0; i < len; i++) u8[i] = binary.charCodeAt(i);
      return new File([new Blob([u8], { type: mime })], filename, { type: mime });
    } catch (e) {
      console.error("dataUrlToFile error:", e);
      return null;
    }
  };

  const handleFileButtonClick = () => {
    // 이미 3장 다 찼으면 공통 처리
    if (files.length >= MAX_FILES) {
      alert(`첨부 파일은 최대 ${MAX_FILES}개까지만 가능합니다.`);
      return;
    }

    // 1) RN이 아닌 경우: 기존처럼 file input 사용
    if (!isRN) {
      fileInputRef.current?.click();
      return;
    }

    // 2) iOS RN도 일단 기존 input 활용 (추후 네이티브 확장 여지)
    if (isIOSRN) {
      if (fileInputRef.current) {
        const input = fileInputRef.current;
        input.value = "";
        setTimeout(() => {
          if (input) input.click();
        }, 200);
      }
      return;
    }

    // 3) Android RN: 커스텀 갤러리/카메라 오픈 (다중 선택)
    if (isAndroidRN) {
      // RN → Web 결과 수신
      const off = addAppMessageListener((msg) => {
        const t = msg?.type;
        const p = msg?.payload || {};

        if (t === "PICK_IMAGE_RESULT") {
          off?.();

          // 여러 장일 수도, 한 장일 수도 있다고 가정
          const list =
            Array.isArray(p.dataUrls) && p.dataUrls.length
              ? p.dataUrls
              : p.dataUrl
                ? [p.dataUrl]
                : [];

          if (!list.length) {
            alert("이미지 결과가 비어 있습니다.");
            return;
          }

          setFiles((prev) => {
            const remain = MAX_FILES - prev.length;
            if (remain <= 0) return prev;

            const sliced = list.slice(0, remain);
            const newFiles = sliced
              .map((d, i) => dataUrlToFile(d, `inquiry_${prev.length + i + 1}.jpg`))
              .filter(Boolean);

            // 🔸 총 용량 10MB 초과 체크
            if (isOverTotalLimit(prev, newFiles)) {
              setIsSizeLimitPopupOpen(true);
              return prev; // 기존 것 유지, 새 건 버림
            }

            return [...newFiles, ...prev];
          });

          return;
        }

        if (t === "PICK_IMAGE_CANCEL" || t === "PICK_IMAGE_ERROR") {
          off?.();
          const msgText = p?.message || (t === "PICK_IMAGE_CANCEL" ? "이미지 선택이 취소되었습니다." : "이미지 선택 중 오류가 발생했습니다.");
          alert(msgText);
        }
      });

      // RN 쪽 갤러리/카메라 열기
      try {
        postToApp({
          type: "OPEN_MEDIA_PICKER",
          payload: {
            at: Date.now(),
            reason: "ads_inquiry",
            multiple: true,                    // 다중 선택 허용 힌트
            max: MAX_FILES - files.length // 남은 칸
          },
        });
      } catch (e) {
        console.error("OPEN_IMAGE_PICKER error:", e);
        off?.();
        alert("이미지 선택을 시작하지 못했습니다.");
      }

      return;
    }
  };


  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;
    const remain = MAX_FILES - files.length;
    const sliced = selected.slice(0, remain);
    // 🔸 총 용량 10MB 초과 체크
    if (isOverTotalLimit(files, sliced)) {
      setIsSizeLimitPopupOpen(true);
      e.target.value = "";
      return;
    }
    setFiles((prev) => [...sliced, ...prev]);
    e.target.value = "";
  };

  const handleRemoveFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = () => {
    if (!inquiryType) {
      alert("문의 종류를 선택해 주세요.");
      return false;
    }
    if (!title.trim()) {
      alert("문의 제목을 입력해 주세요.");
      return false;
    }
    if (!content.trim()) {
      alert("문의 내용을 입력해 주세요.");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (submitting) return;
    if (!validate()) return;

    const fd = new FormData();

    fd.append("user_id", Number(localStorage.getItem("user_id")))
    fd.append("name", name)
    fd.append("phone", phone)
    fd.append("category", inquiryType);
    fd.append("title", title.trim());
    fd.append("content", content.trim());

    if (files[0]) fd.append("file1", files[0]);
    if (files[1]) fd.append("file2", files[1]);
    if (files[2]) fd.append("file3", files[2]);

    try {
      setSubmitting(true);
      const res = await axios.post(`${API}/help/`, fd, {
        // headers: { "Content-Type": "multipart/form-data" },
        timeout: 15000,
        validateStatus: () => true,
      });

      if (res.status >= 200 && res.status < 300) {
        // alert("문의가 접수되었습니다. 빠른 시일 내에 답변드리겠습니다.");
        navigate("/ads/inquiry/history", { state: { name, phone, from, __toast: { key: "inquiry_registered", msg: "등록되었습니다" }, } });
      } else {
        const msg =
          res.data?.detail ||
          res.data?.message ||
          "문의 접수에 실패했습니다. 잠시 후 다시 시도해 주세요.";
        alert(msg);
      }
    } catch (e) {
      alert("네트워크 오류로 문의를 접수하지 못했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleHome = () => {
    const fromPath = from?.pathname || "";
    const isFromMyInfo = fromPath === "/myInfo";

    if (isFromMyInfo) {
      navigate(fromPath, { state: from?.state ?? null, replace: true });
    } else {
      navigate("/ads/main", { state: { tab: "mypage" } });
    }
  };

  const goMyInquiry = () => {
    navigate("/ads/inquiry/history", { state: { name, phone } });
  }

  return (
    <div className="flex flex-col h-dvh bg-surface-default">
      {/* 헤더 */}
      <div ref={headerRef} className="fixed top-0 left-0 right-0 z-10">
        <HeaderIconL
          icon={left}
          onIconClick={handleHome}
          title="고객지원"
        />
        <SupportTabs />
      </div>

      {/* 상단 탭 (자주묻는질문 / 1:1문의 / 고객센터) */}
      <div
        className="px-[16px]"
        style={{ paddingTop: headerSpace, paddingBottom: bottomSpace }}>

        {/* 스크롤 영역 */}
        <div className="flex-1 overflow-y-auto pb-[16px]">
          <div className="flex items-center justify-between pt-[16px] pb-[24px]">
            <h2 className="text-t2 text-text-subtle">1:1 문의</h2>
            <button
              className="text-b2 text-text-subtler underline"
              onClick={goMyInquiry} >나의 문의 내역</button>
          </div>

          {/* 문의 종류 */}
          <div className="pb-[24px]">
            <p className="flex items-center gap-[2px] text-c2 text-text-subtle pb-[6px]">
              <span>문의 종류</span>
              <span className="text-text-danger">*</span>
            </p>
            <SelectBoxB
              value={inquiryType}
              placeholder="문의 종류를 선택해 주세요"
              isOpen={isTypeSheetOpen}
              onClick={() => setIsTypeSheetOpen(true)}
            />
          </div>
          {isTypeSheetOpen && (
            <div className="fixed inset-0 z-50 flex flex-col justify-end bg-black/40">
              {/* 바깥 터치 시 닫기 */}
              <button
                type="button"
                className="flex-1 w-full"
                onClick={() => setIsTypeSheetOpen(false)}
              />

              <div className="bg-surface-light-white rounded-t-[30px] overflow-hidden px-[20px]">
                <div className="pt-[20px] pb-[24px]">
                  <p className="text-t2 text-text-default">문의 종류</p>
                </div>

                {/* 항목 리스트 */}
                <div className="space-y-[22px] pb-[8px]">
                  {INQUIRY_TYPES.map((item) => {
                    const selected = inquiryType === item;
                    return (
                      <button
                        key={item}
                        type="button"
                        onClick={() => {
                          setInquiryType(item);
                          setIsTypeSheetOpen(false);
                        }}
                        className={
                          "w-full text-left text-b1 " +
                          (selected
                            ? "text-primary-600"
                            : "text-text-default")
                        }
                      >
                        {item}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* 내용 */}
          <PlaceholderB
            label="제목"
            placeholder="제목을 입력해 주세요"
            value={title}
            required
            onChange={(e) => setTitle(e.target.value)}
            rightIconMode="clear"
          />
          <div className="pt-[12px]">
            <PlaceholderB
              label=""
              size="lg"
              placeholder="(필수)문의 내용에 이메일, 주소, 핸드폰 번호 등 개인정보가 노출되지 않도록 작성해 주세요"
              maxLength={100}
              showCounter
              value={content}
              onChange={(e) => setContent(e.target.value)}
            />
          </div>

          {/* 첨부 파일 */}
          <div className="py-[16px]">
            <div className="flex items-center justify-between pb-[24px]">
              <p className="text-t4 text-text-subtle">첨부 파일</p>
            </div>

            <Button
              type="border"
              onClick={handleFileButtonClick}
            >
              첨부하기
            </Button>

            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept="image/jpeg,image/png"
              multiple
              onChange={handleFileChange}
            />

            {files.length > 0 && (
              <div className="flex gap-[8px] flex-wrap  pt-[12px]">
                {files.map((file, idx) => {
                  const url = URL.createObjectURL(file);
                  return (
                    <div
                      key={idx}
                      className="relative w-[101.3px] h-[101.3px] rounded-[12px] overflow-hidden border border-border-default"
                    >
                      <img
                        src={url}
                        alt={`첨부파일${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveFile(idx)}
                        className="absolute top-[8px] right-[8px] rounded-full bg-surface-white-dimmed_80 flex items-center justify-center"
                      >
                        <img src={img_delete} alt="삭제" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="pt-[24px] pb-[16px] text-c2 text-text-subtler">
              최대 3개까지 추가 가능해요.<br />
              파일 형식은 jpg,jpeg,png만 가능해요.<br />
              1개당 최대 3MB, 총 10MB 까지 등록이 가능해요.
            </div>
          </div>

          <div className="rounded-[12px] bg-surface-light-subtle px-[16px] py-[12px]">
            <p className="text-c1 text-text-subtle">
              게시물 작성 시 유의사항
            </p>
            <p className="text-c2 text-text-subtler">
              산업안전보호법에 따른 고객 응대 근로자 보호조치에 의거하여 폭언, 욕설 등이 포함된 내용은 상담이 제한될 수 있습니다.
            </p>
          </div>
        </div>

        {showGuide && (
          <>
            <div
              className="fixed inset-0 z-20 bg-surface-dark-dimmed_60"
              onClick={() => setShowGuide(false)}
            />

            <div className="fixed left-0 right-0 bottom-0 z-50">
              <div className="bg-surface-light-white rounded-t-[30px] px-[20px] pt-[16px] pb-[20px] max-h-[80vh] flex flex-col">
                <div ref={headerRef} className="flex items-center justify-between pb-[12px]">
                  <p className="text-t2 text-text-default">
                    개인정보 수집 및 이용 동의
                  </p>
                </div>

                {/* 스크롤 박스 (아래 버튼 높이만큼 여유) */}
                <div className="flex-1 overflow-y-auto pb-[72px]">
                  <div className="text-b2 text-text-subtler">
                    <p>
                      개인정보 수집·이용 동의서<br />
                      제이예스 주식회사(이하 '회사'라 합니다)는 고객님의 개인정보를 중요시하며,
                      "정보통신망 이용촉진 및 정보보호에 관한 법률","개인정보보호법" 등 관련 법규를 준수하고 있습니다.
                      <br />
                      <br />
                      1. 개인정보 수집 및 이용 목적<br />
                      - 고객 문의에 대한 답변 전달
                      <br />
                      <br />
                      2. 수집하는 개인정보 항목<br />
                      - 필수 항목 : 이름, 휴대폰번호, 이메일, 문의유형, 제목, 문의내용
                      <br />
                      <br />
                      3. 개인정보의 보유 및 이용기간<br />
                      이용자의 개인정보는 수집•이용에 대한 동의를 철회하거나 삭제를 요청할 경우
                      또는 개인정보의 수집•이용 목적이 달성되면 지체없이 파기됩니다.
                      다만, 회사는 관계법령의 규정에 의하여 보존할 필요가 있는 경우는
                      해당 법령에서 정한 바에 의하여 개인정보를 보관할 수 있습니다.
                      <br />
                      - 보존 항목 : 이름, 휴대폰번호, 이메일<br />
                      - 보존 근거 : 전자상거래 등에서의 소비자보호에 관한 법률(소비자의 불만 또는 분쟁처리에 관한 기록)<br />
                      - 보존 기간 : 3년
                      <br />
                      <br />
                      4. 동의를 거부할 권리 및 동의 거부에 따른 불이익 <br />
                      이용자는 본 서비스의 개인정보 수집•이용 등에 대한 동의를 거부할 수 있으나,
                      필수항목 수집•이용에 대한 동의 거부 시 본 서비스를 이용할 수 없습니다.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="fixed bottom-0 left-0 right-0 z-[60] px-[20px]">
              <FillButton onClick={() => setShowGuide(false)}>
                확인
              </FillButton>
            </div>
          </>
        )}

        {/* 하단 고정 버튼 */}
        <div
          className="fixed left-0 right-0 bottom-0"
          ref={footerRef}>
          <SingleButton
            onClick={handleSubmit}
            disabled={submitting || !isFormComplete}
            type={isFormComplete ? "fill" : "gray"}
          >
            등록하기
          </SingleButton>
        </div>

        {/* 첨부 이미지 용량 초과 팝업 */}
        <Popup
          isOpen={isSizeLimitPopupOpen}
          onClose={() => setIsSizeLimitPopupOpen(false)}
          title="첨부 이미지"
          subtitle={
            "최대 10MB까지 등록이 가능합니다."
          }
          renderButtons={
            <>
              <FillButton onClick={() => setIsSizeLimitPopupOpen(false)}>확인</FillButton>
            </>
          }
        />
      </div>
    </div>

  );
}
